import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import axios from 'axios';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    Input,
    List,
    Popconfirm,
    Row,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
    Upload,
    message
} from 'antd';
import {
    CameraOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    DeleteOutlined,
    MessageOutlined,
    PlusOutlined,
    ReloadOutlined,
    SendOutlined,
    UploadOutlined
} from '@ant-design/icons';
import Layout from './Layout';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const codeBlockStyle = {
    whiteSpace: 'pre-wrap',
    background: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
    margin: '8px 0 16px'
};

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const MaxMessengerPage = () => {
    const [data, setData] = useState({
        accounts: [],
        logs: [],
        selectors: null,
        stats: {},
        health: {}
    });
    const [messages, setMessages] = useState([]);
    const [selected, setSelected] = useState('');
    const [busy, setBusy] = useState('');
    const [newAccount, setNewAccount] = useState({
        id: '',
        label: ''
    });
    const [compose, setCompose] = useState({
        chatName: 'T2_Gepard_bot',
        message: '',
        taskId: '479843'
    });
    const [photoFile, setPhotoFile] = useState(null);
    const [selectorsDraft, setSelectorsDraft] = useState(null);
    const [gepardJobs, setGepardJobs] = useState([]);
    const [gepardQuery, setGepardQuery] = useState('');
    const [gepardDraft, setGepardDraft] = useState('');
    const [gepardBs, setGepardBs] = useState('');
    const [gepardDates, setGepardDates] = useState(null);
    const gepardFilterRef = useRef({ q: '', bs: '', dateFrom: '', dateTo: '' });
    const [gepardStats, setGepardStats] = useState({
        pending: 0,
        processing: 0,
        done: 0,
        skipped: 0,
        error: 0,
        total: 0
    });
    const [gepardBusy, setGepardBusy] = useState('');

    const current = useMemo(
        () => data.accounts.find(item => item.id === selected),
        [data.accounts, selected]
    );

    const refresh = useCallback(async (silent = false) => {
        try {
            const { data: res } = await axios.get(
                '/api/max/dashboard',
                { headers: authHeaders() }
            );
            const payload = res.data || {};
            setData(payload);
            setSelectorsDraft(payload.selectors || null);
            setSelected(value => value || payload.accounts?.[0]?.id || '');
        } catch (error) {
            if (!silent) {
                message.error(
                    error.response?.data?.message ||
                        error.message ||
                        'Ошибка загрузки MAX'
                );
            }
        }
    }, []);

    const refreshMessages = useCallback(async () => {
        if (!selected) {
            setMessages([]);
            return;
        }
        try {
            const params = {};
            if (compose.chatName?.trim()) {
                params.chatName = compose.chatName.trim();
            }
            const { data: res } = await axios.get(
                `/api/max/accounts/${selected}/messages`,
                { headers: authHeaders(), params }
            );
            setMessages(res.data || []);
        } catch (_) {
            /* ignore */
        }
    }, [selected, compose.chatName]);

    const refreshGepardJobs = useCallback(async (silent = false) => {
        try {
            const filter = gepardFilterRef.current;
            const filtered = Boolean(
                filter.q || filter.bs || filter.dateFrom || filter.dateTo
            );
            const params = { limit: 300 };
            if (filter.q) params.q = filter.q;
            if (filter.bs) params.bs = filter.bs;
            if (filter.dateFrom) params.dateFrom = filter.dateFrom;
            if (filter.dateTo) params.dateTo = filter.dateTo;
            if (!filtered) params.today = 1;
            const { data: res } = await axios.get('/api/max/gepard-jobs', {
                headers: authHeaders(),
                params
            });
            const payload = res.data || {};
            setGepardJobs(
                Array.isArray(payload.jobs)
                    ? payload.jobs
                    : Array.isArray(payload)
                      ? payload
                      : []
            );
            if (payload.todayStats) {
                setGepardStats(payload.todayStats);
            }
        } catch (error) {
            if (!silent) {
                message.error(
                    error.response?.data?.message ||
                        error.message ||
                        'Ошибка загрузки очереди Gepard'
                );
            }
        }
    }, []);

    useEffect(() => {
        refresh();
        refreshGepardJobs(true);
        const timer = setInterval(() => {
            refresh(true);
            refreshGepardJobs(true);
        }, 3000);
        return () => clearInterval(timer);
    }, [refresh, refreshGepardJobs]);

    useEffect(() => {
        refreshMessages();
    }, [refreshMessages]);

    const enqueueTodayGepard = async () => {
        setGepardBusy('enqueue-today');
        try {
            const { data: res } = await axios.post(
                '/api/max/gepard-jobs/enqueue-today',
                {},
                { headers: authHeaders() }
            );
            const r = res.data || {};
            message.success(
                `Сегодня: найдено ${r.scanned || 0}, в очередь ${r.enqueued || 0}, уже были ${r.skipped || 0}`
            );
            await refreshGepardJobs(true);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Не удалось поставить сегодняшние'
            );
        } finally {
            setGepardBusy('');
        }
    };

    const resetGepardBot = async () => {
        setGepardBusy('reset');
        try {
            await axios.post(
                '/api/max/gepard-jobs/reset-bot',
                {
                    accountId: selected || undefined,
                    chatName: compose.chatName || 'T2_Gepard_bot'
                },
                { headers: authHeaders() }
            );
            message.success('В бот отправлен /start');
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Не удалось сбросить бота'
            );
        } finally {
            setGepardBusy('');
        }
    };

    const retryReason = reason => {
        switch (reason) {
            case 'not-estimated':
                return 'Заявка не в статусе «Осмечено»';
            case 'no-tt':
                return 'У заявки нет номера ТТ';
            case 'already-queued':
                return 'Уже стоит в очереди';
            case 'customer-filter':
                return 'Заказчик не в маршруте Гепард';
            default:
                return reason || 'Не удалось поставить в очередь';
        }
    };

    const retryGepardJob = async row => {
        const taskId = row.task_id;
        const label = row.external_id || row.tt_number || taskId;
        setGepardBusy(`retry-${taskId}`);
        try {
            const { data: body } = await axios.post(
                '/api/max/gepard-jobs',
                { taskId, force: true },
                { headers: authHeaders() }
            );
            const payload = body.data || {};
            if (payload.enqueued === false) {
                throw new Error(retryReason(payload.reason));
            }
            message.success(`Заявка ${label} снова в очереди`);
            await refreshGepardJobs(true);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Не удалось повторить'
            );
        } finally {
            setGepardBusy('');
        }
    };

    const searchGepard = value => {
        const next = String(value ?? gepardDraft).trim();
        gepardFilterRef.current.q = next;
        setGepardQuery(next);
        setGepardDraft(next);
        refreshGepardJobs();
    };

    const applyGepardBs = value => {
        const next = String(value ?? gepardBs).trim();
        gepardFilterRef.current.bs = next;
        setGepardBs(next);
        refreshGepardJobs();
    };

    const applyGepardDates = value => {
        setGepardDates(value);
        gepardFilterRef.current.dateFrom = value?.[0] ? value[0].format('YYYY-MM-DD') : '';
        gepardFilterRef.current.dateTo = value?.[1] ? value[1].format('YYYY-MM-DD') : '';
        refreshGepardJobs();
    };

    const resetGepardFilters = () => {
        gepardFilterRef.current = { q: '', bs: '', dateFrom: '', dateTo: '' };
        setGepardQuery('');
        setGepardDraft('');
        setGepardBs('');
        setGepardDates(null);
        refreshGepardJobs();
    };

    const retryUnsent = async () => {
        const rows = gepardJobs.filter(row =>
            ['none', 'skipped', 'error'].includes(row.status)
        );
        if (!rows.length) return;
        setGepardBusy('retry-unsent');
        let queued = 0;
        let failed = 0;
        try {
            for (const row of rows) {
                try {
                    const { data: body } = await axios.post(
                        '/api/max/gepard-jobs',
                        { taskId: row.task_id, force: true },
                        { headers: authHeaders() }
                    );
                    if (body.data?.enqueued === false) failed += 1;
                    else queued += 1;
                } catch (_) {
                    failed += 1;
                }
            }
            message.success(
                failed
                    ? `В очереди: ${queued}. Не поставлены: ${failed}`
                    : `В очереди: ${queued}`
            );
            await refreshGepardJobs(true);
        } finally {
            setGepardBusy('');
        }
    };

    const run = async (name, task, successText) => {
        setBusy(name);
        try {
            await task();
            await refresh(true);
            await refreshMessages();
            if (successText) message.success(successText);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Ошибка'
            );
        } finally {
            setBusy('');
        }
    };

    const sendMessage = async () => {
        if (!current) return;
        const form = new FormData();
        form.append('chatName', compose.chatName);
        form.append('message', compose.message || '');
        if (photoFile) form.append('photo', photoFile);

        await run(
            'send',
            async () => {
                await axios.post(
                    `/api/max/accounts/${current.id}/send`,
                    form,
                    {
                        headers: {
                            ...authHeaders(),
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                setCompose(prev => ({ ...prev, message: '' }));
                setPhotoFile(null);
            },
            'Сообщение отправлено'
        );
    };

    const syncReplies = async () => {
        if (!current || !compose.chatName?.trim()) return;
        setBusy('sync-replies');
        try {
            const { data: res } = await axios.post(
                `/api/max/accounts/${current.id}/sync-replies`,
                { chatName: compose.chatName.trim() },
                { headers: authHeaders() }
            );
            await refresh(true);
            await refreshMessages();
            const created = res.data?.created ?? 0;
            message.success(
                created
                    ? `Новых ответов: ${created}`
                    : 'Новых ответов нет'
            );
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Ошибка'
            );
        } finally {
            setBusy('');
        }
    };

    const startGepardTtSearch = async () => {
        if (!current) return;
        setBusy('gepard-tt');
        try {
            const body = {
                chatName: compose.chatName.trim() || 'T2_Gepard_bot'
            };
            if (compose.taskId?.trim()) {
                body.taskId = Number(compose.taskId.trim());
            }
            if (compose.message?.trim()) {
                body.ttNumber = compose.message.trim();
            }
            const { data: res } = await axios.post(
                `/api/max/accounts/${current.id}/gepard-tt-search`,
                body,
                { headers: authHeaders() }
            );
            await refresh(true);
            await refreshMessages();
            const tt = res.data?.task?.ttNumber || body.ttNumber || '—';
            message.success(
                `Gepard: клик «Поиск…» + ТТ ${tt} отправлены`
            );
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Ошибка Gepard'
            );
        } finally {
            setBusy('');
        }
    };

    const logColumns = [
        {
            title: 'Время',
            dataIndex: 'at',
            width: 170,
            render: v =>
                v ? new Date(v).toLocaleString('ru-RU') : '—'
        },
        {
            title: 'Аккаунт',
            dataIndex: 'accountId',
            width: 140,
            render: v => v || '—'
        },
        {
            title: 'Действие',
            dataIndex: 'action',
            width: 140
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            width: 90,
            render: v => (
                <Tag color={v === 'ok' ? 'green' : 'red'}>{v}</Tag>
            )
        },
        {
            title: 'Детали',
            dataIndex: 'details',
            render: details => {
                if (!details) return '—';
                return (
                    details.error ||
                    details.chatName ||
                    details.preview ||
                    details.note ||
                    JSON.stringify(details)
                );
            }
        }
    ];

    const messageColumns = [
        {
            title: 'Время',
            dataIndex: 'created_at',
            width: 170,
            render: (v, row) => {
                const ts = row.sent_at || v || row.createdAt;
                return ts ? new Date(ts).toLocaleString('ru-RU') : '—';
            }
        },
        {
            title: 'Напр.',
            dataIndex: 'direction',
            width: 100,
            render: v =>
                v === 'in' ? (
                    <Tag color="blue">входящее</Tag>
                ) : (
                    <Tag>исходящее</Tag>
                )
        },
        {
            title: 'Чат',
            dataIndex: 'chat_name'
        },
        {
            title: 'Текст',
            dataIndex: 'message_text',
            ellipsis: true,
            render: v => v || '—'
        },
        {
            title: 'Фото',
            dataIndex: 'has_photo',
            width: 80,
            render: v => (v ? 'да' : '—')
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            width: 110,
            render: (v, row) => (
                <Tag
                    color={
                        v === 'sent' || v === 'received'
                            ? 'green'
                            : v === 'error'
                              ? 'red'
                              : 'gold'
                    }
                >
                    {v}
                    {row.error_text ? `: ${row.error_text}` : ''}
                </Tag>
            )
        }
    ];

    const gepardStatusColor = status => {
        switch (status) {
            case 'done':
                return 'green';
            case 'error':
                return 'red';
            case 'processing':
                return 'blue';
            case 'pending':
                return 'gold';
            case 'skipped':
                return 'default';
            default:
                return 'default';
        }
    };

    const gepardStatusLabel = status => {
        switch (status) {
            case 'done':
                return 'ушла';
            case 'error':
                return 'ошибка';
            case 'processing':
                return 'отправляется';
            case 'pending':
                return 'в очереди';
            case 'skipped':
                return 'не ушла';
            case 'none':
                return 'не ставилась';
            default:
                return status || '—';
        }
    };

    const unsentCount = gepardJobs.filter(row =>
        ['none', 'skipped', 'error'].includes(row.status)
    ).length;

    const gepardJobColumns = [
        {
            title: 'Время',
            dataIndex: 'created_at',
            width: 150,
            render: (v, row) => {
                const ts = row.finished_at || row.started_at || v || row.createdAt;
                return ts ? new Date(ts).toLocaleString('ru-RU') : '—';
            }
        },
        {
            title: 'Аккаунт',
            dataIndex: 'account_id',
            width: 130,
            render: v => v || '—'
        },
        {
            title: 'Заявка',
            dataIndex: 'external_id',
            width: 100,
            render: (v, row) => v || row.task_id || '—'
        },
        {
            title: 'БС',
            dataIndex: 'bs_number',
            width: 100,
            render: v => v || '—'
        },
        {
            title: 'ТТ',
            dataIndex: 'tt_number',
            width: 130,
            render: v => v || '—'
        },
        {
            title: 'Заказчик',
            dataIndex: 'zakazchik',
            ellipsis: true,
            render: v => v || '—'
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            width: 130,
            render: v => (
                <Tag color={gepardStatusColor(v)}>{gepardStatusLabel(v)}</Tag>
            )
        },
        {
            title: 'Попытки',
            dataIndex: 'attempts',
            width: 80
        },
        {
            title: 'Ошибка / детали',
            dataIndex: 'last_error',
            render: (v, row) => {
                const bits = [];
                if (row.ever_sent && row.status !== 'done') bits.push('раньше уходила');
                if (row.gepard_tt) bits.push(`Гепард ТТ ${row.gepard_tt}`);
                if (row.photos) bits.push(`${row.photos} фото`);
                if (row.via) bits.push(row.via === 'bs' ? 'поиск по БС' : 'поиск по ТТ');
                if (v) bits.push(v);
                else if (row.result_json?.reason) bits.push(row.result_json.reason);
                if (row.status === 'none') {
                    bits.push(
                        row.task_status && row.task_status !== 'Осмечено'
                            ? `Не ушла: в CRM «${row.task_status}». Отчёт ставится только из «Осмечено»`
                            : 'В очередь не ставилась'
                    );
                }
                return bits.join(' · ') || '—';
            }
        },
        {
            title: '',
            key: 'actions',
            width: 130,
            render: (_, row) => {
                if (row.status === 'pending' || row.status === 'processing') return null;
                if (!row.task_id) return null;
                const again = row.status === 'done';
                const button = (
                    <Button
                        size="small"
                        disabled={!!gepardBusy}
                        onClick={again ? undefined : () => retryGepardJob(row)}
                    >
                        {again ? 'Ещё раз' : 'Отправить'}
                    </Button>
                );
                if (!again) return button;
                return (
                    <Popconfirm
                        title="Отчёт уже уходил. Поставить в очередь ещё раз?"
                        okText="Отправить"
                        cancelText="Отмена"
                        onConfirm={() => retryGepardJob(row)}
                    >
                        {button}
                    </Popconfirm>
                );
            }
        }
    ];

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            <MessageOutlined /> MAX-мессенджер
                        </Title>
                        <Text type="secondary">
                            Отправка сообщений и фото через web.max.ru
                            (только администраторы)
                        </Text>
                    </div>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => refresh()}
                    >
                        Обновить
                    </Button>
                </Space>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                    {[
                        ['Аккаунтов', data.stats.totalAccounts],
                        ['Запущено', data.stats.runningAccounts],
                        ['Авторизовано', data.stats.loggedInAccounts],
                        ['Отправлено сегодня', data.stats.sentToday]
                    ].map(([label, value]) => (
                        <Col span={6} key={label}>
                            <Card size="small">
                                <Text type="secondary">{label}</Text>
                                <Title level={3} style={{ margin: 0 }}>
                                    {value || 0}
                                </Title>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Tabs
                    items={[
                        {
                            key: 'messages',
                            label: 'Сообщения',
                            children: (
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Card
                                            title="Аккаунты"
                                            size="small"
                                            extra={
                                                <Button
                                                    size="small"
                                                    icon={<PlusOutlined />}
                                                    onClick={() =>
                                                        document
                                                            .getElementById(
                                                                'max-new-account'
                                                            )
                                                            ?.scrollIntoView({
                                                                behavior:
                                                                    'smooth'
                                                            })
                                                    }
                                                >
                                                    Новый
                                                </Button>
                                            }
                                        >
                                            <List
                                                dataSource={data.accounts}
                                                locale={{
                                                    emptyText:
                                                        'Добавьте первый аккаунт'
                                                }}
                                                renderItem={account => (
                                                    <List.Item
                                                        onClick={() =>
                                                            setSelected(
                                                                account.id
                                                            )
                                                        }
                                                        style={{
                                                            cursor: 'pointer',
                                                            background:
                                                                selected ===
                                                                account.id
                                                                    ? '#e6f4ff'
                                                                    : undefined,
                                                            padding: 12,
                                                            borderRadius: 8
                                                        }}
                                                    >
                                                        <List.Item.Meta
                                                            title={
                                                                account.label
                                                            }
                                                            description={
                                                                account.id
                                                            }
                                                        />
                                                        <Tag
                                                            color={
                                                                account.loggedIn
                                                                    ? 'green'
                                                                    : account.running
                                                                      ? 'gold'
                                                                      : 'default'
                                                            }
                                                        >
                                                            {account.loggedIn
                                                                ? 'подключён'
                                                                : account.running
                                                                  ? 'ожидание'
                                                                  : 'offline'}
                                                        </Tag>
                                                    </List.Item>
                                                )}
                                            />
                                            {current && (
                                                <Space
                                                    wrap
                                                    style={{ marginTop: 12 }}
                                                >
                                                    <Button
                                                        type="primary"
                                                        disabled={!!busy}
                                                        onClick={() =>
                                                            run(
                                                                'open',
                                                                () =>
                                                                    axios.post(
                                                                        `/api/max/accounts/${current.id}/open`,
                                                                        {},
                                                                        {
                                                                            headers:
                                                                                authHeaders()
                                                                        }
                                                                    ),
                                                                'Окно MAX открыто. Войдите вручную в Chromium на VNC.'
                                                            )
                                                        }
                                                    >
                                                        Открыть MAX
                                                    </Button>
                                                    <Button
                                                        disabled={!!busy}
                                                        onClick={() =>
                                                            run(
                                                                'check',
                                                                () =>
                                                                    axios.post(
                                                                        `/api/max/accounts/${current.id}/check`,
                                                                        {},
                                                                        {
                                                                            headers:
                                                                                authHeaders()
                                                                        }
                                                                    ),
                                                                'Статус обновлён'
                                                            )
                                                        }
                                                    >
                                                        Проверить
                                                    </Button>
                                                    {current.running &&
                                                        !current.loggedIn && (
                                                            <Button
                                                                icon={
                                                                    <CheckCircleOutlined />
                                                                }
                                                                disabled={
                                                                    !!busy
                                                                }
                                                                onClick={() =>
                                                                    run(
                                                                        'confirm',
                                                                        () =>
                                                                            axios.post(
                                                                                `/api/max/accounts/${current.id}/confirm-login`,
                                                                                {},
                                                                                {
                                                                                    headers:
                                                                                        authHeaders()
                                                                                }
                                                                            ),
                                                                        'Вход подтверждён'
                                                                    )
                                                                }
                                                            >
                                                                Подтвердить
                                                                вход
                                                            </Button>
                                                        )}
                                                    <Button
                                                        icon={
                                                            <CloseOutlined />
                                                        }
                                                        disabled={!!busy}
                                                        onClick={() =>
                                                            run(
                                                                'close',
                                                                () =>
                                                                    axios.post(
                                                                        `/api/max/accounts/${current.id}/close`,
                                                                        {},
                                                                        {
                                                                            headers:
                                                                                authHeaders()
                                                                        }
                                                                    ),
                                                                'Сессия закрыта'
                                                            )
                                                        }
                                                    >
                                                        Закрыть
                                                    </Button>
                                                    <Button
                                                        icon={
                                                            <CameraOutlined />
                                                        }
                                                        disabled={!!busy}
                                                        onClick={() =>
                                                            run(
                                                                'shot',
                                                                async () => {
                                                                    const response =
                                                                        await axios.get(
                                                                            `/api/max/accounts/${current.id}/screenshot`,
                                                                            {
                                                                                headers:
                                                                                    authHeaders(),
                                                                                responseType:
                                                                                    'blob'
                                                                            }
                                                                        );
                                                                    const url =
                                                                        window.URL.createObjectURL(
                                                                            response.data
                                                                        );
                                                                    window.open(
                                                                        url,
                                                                        '_blank'
                                                                    );
                                                                },
                                                                'Скриншот готов'
                                                            )
                                                        }
                                                    >
                                                        Скриншот
                                                    </Button>
                                                </Space>
                                            )}
                                        </Card>
                                    </Col>
                                    <Col span={16}>
                                        <Card
                                            title={
                                                current
                                                    ? `Новое сообщение от «${current.label}»`
                                                    : 'Выберите аккаунт'
                                            }
                                            size="small"
                                        >
                                            <Form layout="vertical">
                                                <Form.Item label="Название чата">
                                                    <Input
                                                        value={
                                                            compose.chatName
                                                        }
                                                        placeholder="T2_Gepard_bot"
                                                        onChange={e =>
                                                            setCompose({
                                                                ...compose,
                                                                chatName:
                                                                    e.target
                                                                        .value
                                                            })
                                                        }
                                                    />
                                                </Form.Item>
                                                <Form.Item label="ID заявки (для Gepard ТТ)">
                                                    <Input
                                                        value={compose.taskId}
                                                        placeholder="479843"
                                                        onChange={e =>
                                                            setCompose({
                                                                ...compose,
                                                                taskId: e
                                                                    .target
                                                                    .value
                                                            })
                                                        }
                                                    />
                                                </Form.Item>
                                                <Form.Item label="Текст / номер ТТ">
                                                    <TextArea
                                                        rows={6}
                                                        maxLength={5000}
                                                        value={
                                                            compose.message
                                                        }
                                                        placeholder="Если пусто — ТТ возьмётся из заявки"
                                                        onChange={e =>
                                                            setCompose({
                                                                ...compose,
                                                                message:
                                                                    e.target
                                                                        .value
                                                            })
                                                        }
                                                    />
                                                </Form.Item>
                                                <Form.Item label="Фото (опционально)">
                                                    <Upload
                                                        beforeUpload={file => {
                                                            setPhotoFile(
                                                                file
                                                            );
                                                            return false;
                                                        }}
                                                        maxCount={1}
                                                        accept="image/*"
                                                        onRemove={() =>
                                                            setPhotoFile(null)
                                                        }
                                                        fileList={
                                                            photoFile
                                                                ? [
                                                                      {
                                                                          uid: '1',
                                                                          name: photoFile.name,
                                                                          status: 'done'
                                                                      }
                                                                  ]
                                                                : []
                                                        }
                                                    >
                                                        <Button
                                                            icon={
                                                                <UploadOutlined />
                                                            }
                                                        >
                                                            Выбрать фото
                                                        </Button>
                                                    </Upload>
                                                </Form.Item>
                                                <Space wrap>
                                                <Button
                                                    type="primary"
                                                    icon={<SendOutlined />}
                                                    disabled={
                                                        !current ||
                                                        busy === 'send' ||
                                                        !compose.chatName ||
                                                        (!compose.message &&
                                                            !photoFile)
                                                    }
                                                    onClick={sendMessage}
                                                >
                                                    {busy === 'send'
                                                        ? 'Отправка…'
                                                        : 'Отправить'}
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    ghost
                                                    disabled={
                                                        !current ||
                                                        busy ===
                                                            'gepard-tt' ||
                                                        (!compose.taskId &&
                                                            !compose.message)
                                                    }
                                                    onClick={
                                                        startGepardTtSearch
                                                    }
                                                >
                                                    {busy === 'gepard-tt'
                                                        ? 'Gepard…'
                                                        : 'Gepard: поиск по ТТ'}
                                                </Button>
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    disabled={
                                                        !current ||
                                                        busy ===
                                                            'sync-replies' ||
                                                        !compose.chatName
                                                    }
                                                    onClick={syncReplies}
                                                >
                                                    {busy === 'sync-replies'
                                                        ? 'Читаю…'
                                                        : 'Получить ответы'}
                                                </Button>
                                                </Space>
                                            </Form>
                                        </Card>
                                        <Card
                                            title="Переписка"
                                            size="small"
                                            style={{ marginTop: 16 }}
                                            extra={
                                                <Button
                                                    size="small"
                                                    icon={
                                                        <ReloadOutlined />
                                                    }
                                                    onClick={refreshMessages}
                                                >
                                                    Обновить
                                                </Button>
                                            }
                                        >
                                            <Table
                                                size="small"
                                                rowKey="id"
                                                columns={messageColumns}
                                                dataSource={messages}
                                                pagination={{
                                                    pageSize: 8
                                                }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            )
                        },
                        {
                            key: 'accounts',
                            label: 'Аккаунты',
                            children: (
                                <Row gutter={16}>
                                    <Col span={10}>
                                        <Card
                                            id="max-new-account"
                                            title="Новый аккаунт"
                                            size="small"
                                        >
                                            <Form
                                                layout="vertical"
                                                onFinish={() =>
                                                    run(
                                                        'create',
                                                        async () => {
                                                            await axios.post(
                                                                '/api/max/accounts',
                                                                newAccount,
                                                                {
                                                                    headers:
                                                                        authHeaders()
                                                                }
                                                            );
                                                            setNewAccount({
                                                                id: '',
                                                                label: ''
                                                            });
                                                        },
                                                        'Аккаунт создан'
                                                    )
                                                }
                                            >
                                                <Form.Item label="ID профиля">
                                                    <Input
                                                        required
                                                        pattern="[A-Za-z0-9_-]+"
                                                        value={newAccount.id}
                                                        placeholder="manager_1"
                                                        onChange={e =>
                                                            setNewAccount({
                                                                ...newAccount,
                                                                id: e.target
                                                                    .value
                                                            })
                                                        }
                                                    />
                                                </Form.Item>
                                                <Form.Item label="Название">
                                                    <Input
                                                        required
                                                        value={
                                                            newAccount.label
                                                        }
                                                        placeholder="Менеджер 1"
                                                        onChange={e =>
                                                            setNewAccount({
                                                                ...newAccount,
                                                                label: e
                                                                    .target
                                                                    .value
                                                            })
                                                        }
                                                    />
                                                </Form.Item>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    disabled={
                                                        busy === 'create'
                                                    }
                                                >
                                                    Создать
                                                </Button>
                                            </Form>
                                        </Card>
                                    </Col>
                                    <Col span={14}>
                                        <Card
                                            title="Все профили"
                                            size="small"
                                        >
                                            <List
                                                dataSource={data.accounts}
                                                renderItem={account => (
                                                    <List.Item
                                                        actions={[
                                                            <Button
                                                                key="open"
                                                                size="small"
                                                                onClick={() =>
                                                                    run(
                                                                        'open',
                                                                        () =>
                                                                            axios.post(
                                                                                `/api/max/accounts/${account.id}/open`,
                                                                                {},
                                                                                {
                                                                                    headers:
                                                                                        authHeaders()
                                                                                }
                                                                            )
                                                                    )
                                                                }
                                                            >
                                                                Открыть
                                                            </Button>,
                                                            <Button
                                                                key="del"
                                                                danger
                                                                size="small"
                                                                icon={
                                                                    <DeleteOutlined />
                                                                }
                                                                onClick={() => {
                                                                    if (
                                                                        !window.confirm(
                                                                            'Удалить профиль и браузерную сессию?'
                                                                        )
                                                                    ) {
                                                                        return;
                                                                    }
                                                                    run(
                                                                        'delete',
                                                                        () =>
                                                                            axios.delete(
                                                                                `/api/max/accounts/${account.id}`,
                                                                                {
                                                                                    headers:
                                                                                        authHeaders()
                                                                                }
                                                                            ),
                                                                        'Удалено'
                                                                    );
                                                                }}
                                                            >
                                                                Удалить
                                                            </Button>
                                                        ]}
                                                    >
                                                        <List.Item.Meta
                                                            title={
                                                                account.label
                                                            }
                                                            description={
                                                                account.id
                                                            }
                                                        />
                                                    </List.Item>
                                                )}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            )
                        },
                        {
                            key: 'settings',
                            label: 'Селекторы',
                            children: selectorsDraft ? (
                                <Card size="small">
                                    {Object.entries(selectorsDraft).map(
                                        ([key, values]) => (
                                            <Form.Item
                                                key={key}
                                                label={key}
                                            >
                                                <TextArea
                                                    rows={4}
                                                    value={(
                                                        values || []
                                                    ).join('\n')}
                                                    onChange={e =>
                                                        setSelectorsDraft({
                                                            ...selectorsDraft,
                                                            [key]: e.target.value
                                                                .split('\n')
                                                                .map(x =>
                                                                    x.trim()
                                                                )
                                                                .filter(
                                                                    Boolean
                                                                )
                                                        })
                                                    }
                                                />
                                            </Form.Item>
                                        )
                                    )}
                                    <Button
                                        type="primary"
                                        onClick={() =>
                                            run(
                                                'settings',
                                                () =>
                                                    axios.put(
                                                        '/api/max/selectors',
                                                        selectorsDraft,
                                                        {
                                                            headers:
                                                                authHeaders()
                                                        }
                                                    ),
                                                'Селекторы сохранены'
                                            )
                                        }
                                    >
                                        Сохранить
                                    </Button>
                                </Card>
                            ) : (
                                <Text type="secondary">
                                    Селекторы не загружены
                                </Text>
                            )
                        },
                        {
                            key: 'gepard',
                            label: 'Gepard отчёты',
                            children: (
                                <>
                                    <Row gutter={12} style={{ marginBottom: 12 }}>
                                        {[
                                            ['Сегодня всего', gepardStats.total],
                                            ['Отправлено', gepardStats.done],
                                            ['В очереди', gepardStats.pending],
                                            ['Сейчас', gepardStats.processing],
                                            ['Ошибки', gepardStats.error],
                                            ['Пропуск', gepardStats.skipped]
                                        ].map(([label, value]) => (
                                            <Col span={4} key={label}>
                                                <Card size="small">
                                                    <Text type="secondary">
                                                        {label}
                                                    </Text>
                                                    <Title
                                                        level={4}
                                                        style={{ margin: 0 }}
                                                    >
                                                        {value || 0}
                                                    </Title>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                    <Card
                                        size="small"
                                        title={
                                            gepardQuery || gepardBs || gepardDates
                                                ? [
                                                      gepardQuery
                                                          ? `Поиск: ${gepardQuery}`
                                                          : '',
                                                      gepardBs
                                                          ? `БС: ${gepardBs}`
                                                          : '',
                                                      gepardDates?.[0] &&
                                                      gepardDates?.[1]
                                                          ? `${gepardDates[0].format('DD.MM.YYYY')}–${gepardDates[1].format('DD.MM.YYYY')}`
                                                          : ''
                                                  ]
                                                      .filter(Boolean)
                                                      .join(' · ')
                                                : 'Очередь отправки в T2_Gepard_bot (сегодня)'
                                        }
                                        extra={
                                            <Space>
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    disabled={!!gepardBusy}
                                                    onClick={() =>
                                                        refreshGepardJobs()
                                                    }
                                                >
                                                    Обновить
                                                </Button>
                                                <Button
                                                    disabled={!!gepardBusy}
                                                    onClick={resetGepardBot}
                                                >
                                                    /start боту
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    disabled={!!gepardBusy}
                                                    loading={
                                                        gepardBusy ===
                                                        'enqueue-today'
                                                    }
                                                    onClick={enqueueTodayGepard}
                                                >
                                                    Поставить сегодняшние
                                                </Button>
                                            </Space>
                                        }
                                    >
                                        <Paragraph type="secondary">
                                            Авто: при переходе в «Осмечено».
                                            Москва — 79225588018, скан за
                                            сегодня (МСК). Екатеринбург —
                                            79022917331 с 20.10.2026. Великий
                                            Новгород — тот же маршрут, что
                                            Москва. Без фильтров — живая очередь
                                            и отправки за сегодня (по дате
                                            отправки, не постановки). Поиск —
                                            ТТ или номер заявки; период — дата
                                            отправки (МСК). «Ушла» — бот принял
                                            отчёт. «Не ставилась» — в Gepard
                                            уходит только из «Осмечено».
                                        </Paragraph>
                                        <Space wrap style={{ marginBottom: 12 }}>
                                            <Input.Search
                                                allowClear
                                                style={{ width: 360 }}
                                                placeholder="ТТ или номер заявки, можно несколько"
                                                value={gepardDraft}
                                                onChange={event =>
                                                    setGepardDraft(event.target.value)
                                                }
                                                onSearch={searchGepard}
                                                enterButton="Найти"
                                            />
                                            <Input.Search
                                                allowClear
                                                style={{ width: 220 }}
                                                placeholder="Номер БС"
                                                value={gepardBs}
                                                onChange={event => {
                                                    const next = event.target.value;
                                                    setGepardBs(next);
                                                    if (!next) applyGepardBs('');
                                                }}
                                                onSearch={applyGepardBs}
                                                enterButton="БС"
                                            />
                                            <RangePicker
                                                value={gepardDates}
                                                format="DD.MM.YYYY"
                                                placeholder={['Дата с', 'Дата по']}
                                                onChange={applyGepardDates}
                                                allowClear
                                            />
                                            {gepardQuery || gepardBs || gepardDates ? (
                                                <Button onClick={resetGepardFilters}>
                                                    Сбросить
                                                </Button>
                                            ) : null}
                                            {(gepardQuery || gepardBs || gepardDates) &&
                                            unsentCount ? (
                                                <Popconfirm
                                                    title={`Поставить в очередь ${unsentCount} заявок, которые не ушли?`}
                                                    okText="Отправить"
                                                    cancelText="Отмена"
                                                    onConfirm={retryUnsent}
                                                >
                                                    <Button
                                                        type="primary"
                                                        disabled={!!gepardBusy}
                                                        loading={
                                                            gepardBusy ===
                                                            'retry-unsent'
                                                        }
                                                    >
                                                        Отправить не ушедшие ({unsentCount})
                                                    </Button>
                                                </Popconfirm>
                                            ) : null}
                                        </Space>
                                        <Table
                                            size="small"
                                            rowKey="id"
                                            columns={gepardJobColumns}
                                            dataSource={gepardJobs}
                                            pagination={{ pageSize: 20 }}
                                            scroll={{ x: 1200 }}
                                            locale={{
                                                emptyText:
                                                    gepardQuery ||
                                                    gepardBs ||
                                                    gepardDates
                                                        ? 'Ничего не найдено'
                                                        : 'Пока нет отправок за сегодня'
                                            }}
                                        />
                                    </Card>
                                </>
                            )
                        },
                        {
                            key: 'logs',
                            label: 'Журнал',
                            children: (
                                <Card size="small">
                                    <Table
                                        size="small"
                                        rowKey="id"
                                        columns={logColumns}
                                        dataSource={data.logs || []}
                                        pagination={{ pageSize: 20 }}
                                    />
                                </Card>
                            )
                        },
                        {
                            key: 'guide',
                            label: 'Инструкция',
                            children: (
                                <Card size="small" title="TigerVNC + SSH-туннель">
                                    <Paragraph>
                                        Чтобы вручную войти в MAX, на вашем
                                        компьютере нужен доступ к рабочему
                                        столу сервера через TigerVNC. Туннель
                                        пробрасывает VNC-порт на{' '}
                                        <Text code>localhost</Text>.
                                    </Paragraph>

                                    <Paragraph type="secondary">
                                        Важно: TigerVNC Viewer (не RealVNC) —
                                        иначе часто «соединение прервано
                                        сервером». В Options → Encryption =
                                        Prefer off.
                                    </Paragraph>

                                    <Title level={5}>
                                        Быстрый способ (одна сеть)
                                    </Title>
                                    <Paragraph>
                                        Если ПК в той же сети, что сервер
                                        (например 10.10.11.x), туннель не
                                        нужен. В Viewer укажите:
                                    </Paragraph>
                                    <pre style={codeBlockStyle}>
{`10.10.11.2:5901`}
                                    </pre>

                                    <Title level={5}>
                                        1. SSH-туннель (если не в одной сети)
                                    </Title>
                                    <Paragraph>
                                        В терминале (Linux / macOS / Windows
                                        OpenSSH) выполните:
                                    </Paragraph>
                                    <pre style={codeBlockStyle}>
{`ssh -L 5901:127.0.0.1:5901 USER@SERVER_IP

# пример:
# ssh -L 5901:127.0.0.1:5901 crm@10.10.11.2`}
                                    </pre>
                                    <Paragraph type="secondary">
                                        Окно SSH не закрывайте — пока туннель
                                        активен, VNC доступен локально.
                                    </Paragraph>

                                    <Title level={5}>
                                        2. Подключение TigerVNC Viewer
                                    </Title>
                                    <Paragraph>
                                        Установите TigerVNC Viewer и
                                        подключитесь к:
                                    </Paragraph>
                                    <pre style={codeBlockStyle}>
{`127.0.0.1:5901`}
                                    </pre>
                                    <Paragraph>
                                        Введите пароль VNC. Откроется рабочий
                                        стол сервера (XFCE).
                                    </Paragraph>

                                    <Title level={5}>
                                        3. Вход в MAX
                                    </Title>
                                    <Paragraph style={{ marginBottom: 0 }}>
                                        В CRM на вкладке «Сообщения» нажмите
                                        «Открыть MAX» → в окне Chromium на
                                        VNC-столе войдите в аккаунт вручную →
                                        вернитесь в CRM и нажмите «Подтвердить
                                        вход». После этого можно отправлять
                                        сообщения и фото. Туннель можно
                                        закрыть — фоновая отправка работает
                                        без него.
                                    </Paragraph>
                                </Card>
                            )
                        }
                    ]}
                />
            </div>
        </Layout>
    );
};

export default MaxMessengerPage;
